import { AxiosResponse } from 'axios';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import {
    AgentApiInterface,
    AgentContractStateEnum,
    ContractEditPayloadApiInterface,
    ContractPayloadApiInterface,
    InsurancePayloadApiInterface,
    InsuranceDatePayloadApiInterface,
} from 'submodules/api_middleware';
import { FormResponseData } from 'services/./user.service';
import fetcher, {
    fetcherFull,
    MethodEnum,
} from 'utils/helpers/browser/newFetcher';

export const getAgents = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse<AgentApiInterface[]>> => {
    return await fetcher(`/api/agent/list`, MethodEnum.GET, query);
};

export const createAgentContract = async (
    agentId: number,
    data: ContractPayloadApiInterface
): Promise<FormResponseData> => {
    return fetcherFull(
        `/api/agent/${agentId}/contracts/create`,
        MethodEnum.POST,
        undefined,
        {
            data,
        }
    );
};

export const editAgentContract = async (
    agentId: number,
    contractId: number,
    data: ContractEditPayloadApiInterface
): Promise<FormResponseData> => {
    return fetcherFull(
        `/api/agent/${agentId}/contracts/${contractId}/edit`,
        MethodEnum.POST,
        undefined,
        {
            data,
        }
    );
};

export const markAgentContract = async (
    agentId: number,
    contractId: number,
    status: AgentContractStateEnum
): Promise<FormResponseData> => {
    return fetcherFull(
        `/api/agent/${agentId}/contracts/${contractId}/mark-as/${status}`,
        MethodEnum.GET
    );
};

export const createAgentInsurance = async (
    agentId: number,
    data: InsurancePayloadApiInterface
): Promise<FormResponseData> => {
    return fetcherFull(
        `/api/agent/${agentId}/insurances/create`,
        MethodEnum.POST,
        undefined,
        {
            data,
        }
    );
};

export const editAgentInsurance = async (
    agentId: number,
    insuranceId: number,
    data: InsurancePayloadApiInterface
): Promise<FormResponseData> => {
    return fetcherFull(
        `/api/agent/${agentId}/insurances/${insuranceId}/edit`,
        MethodEnum.POST,
        undefined,
        {
            data,
        }
    );
};

export const uploadAgentContractDocument = async (
    agentId: number,
    contractId: number,
    document: File
): Promise<FormResponseData> => {
    const formData = new FormData();
    formData.append('document', document);

    return await fetcherFull<FormResponseData>(
        `/api/agent/${agentId}/contracts/${contractId}/document/upload`,
        MethodEnum.POST,
        null,
        formData
    );
};

export const uploadAgentInsuranceDocument = async (
    agentId: number,
    insuranceId: number,
    document: File
): Promise<FormResponseData> => {
    const formData = new FormData();
    formData.append('document', document);

    return await fetcherFull<FormResponseData>(
        `/api/agent/${agentId}/insurances/${insuranceId}/document/upload`,
        MethodEnum.POST,
        null,
        formData
    );
};

export const deleteAgentContractDocument = async (
    agentId: number,
    contractId: number,
    documentId: number
): Promise<FormResponseData> => {
    return await fetcherFull<FormResponseData>(
        `/api/agent/${agentId}/contracts/${contractId}/document/${documentId}/delete`
    );
};

export const deleteAgentInsuranceDocument = async (
    agentId: number,
    insuranceId: number,
    documentId: number
): Promise<FormResponseData> => {
    return await fetcherFull<FormResponseData>(
        `/api/agent/${agentId}/insurances/${insuranceId}/document/${documentId}/delete`
    );
};

export const createAgentInsuranceDate = async (
    agentId: number,
    insuranceId: number,
    data: InsuranceDatePayloadApiInterface
): Promise<FormResponseData> => {
    return fetcher(
        `/api/agent/${agentId}/insurances/${insuranceId}/dates/create`,
        MethodEnum.POST,
        undefined,
        {
            data,
        }
    );
};

export const deleteAgentInsuranceDate = async (
    agentId: string,
    insuranceId: string,
    dateId: string
): Promise<FormResponseData> => {
    return fetcher(
        `/api/agent/${agentId}/insurances/${insuranceId}/dates/${dateId}/delete`
    );
};

export const editAgentInsuranceDate = async (
    agentId: number,
    insuranceId: number,
    dateId: number,
    data: InsuranceDatePayloadApiInterface
): Promise<FormResponseData> => {
    return fetcher(
        `/api/agent/${agentId}/insurances/${insuranceId}/dates/${dateId}/edit`,
        MethodEnum.PUT,
        undefined,
        {
            data,
        }
    );
};

export const uploadAgentInsuranceDateDocument = async (
    agentId: number,
    insuranceId: number,
    dateId: number,
    document: File
): Promise<FormResponseData> => {
    const formData = new FormData();
    formData.append('document', document);

    return await fetcher<FormResponseData>(
        `/api/agent/${agentId}/insurances/${insuranceId}/dates/${dateId}/document/upload`,
        MethodEnum.POST,
        null,
        formData
    );
};

export const deleteAgentInsuranceDateDocument = async (
    agentId: number,
    insuranceId: number,
    dateId: number,
    documentId: string
): Promise<FormResponseData> => {
    return await fetcher<FormResponseData>(
        `/api/agent/${agentId}/insurances/${insuranceId}/dates/${dateId}/document/${documentId}/delete`
    );
};
